import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseQueryCreator } from "redux/baseQuery";
import {
  BulkPaymentItemListResponse,
  BulkPaymentItemRequest,
  DatasetDownloadRequest,
  DatasetDownloadResponse,
  FileDetailsReponse,
  FileDetailsRequest,
  HistoryFileUploadedRequest,
  HistoryFileUploadedResponse,
  UploadFileResponse,
} from "./types";
import { DataLoaderDataSets } from "common";
import env from "env";

const { dataLoaderService } = env.api.baseUrl;

const baseQuery = fetchBaseQuery({
  baseUrl: dataLoaderService,
  prepareHeaders: (headers, { getState }: any) => {
    const token = getState().auth.accessToken;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

const getMetaData = (
  datasetName: string,
  organizationNumber: string | undefined,
  orgId: string | undefined
) => {
  switch (datasetName) {
    case DataLoaderDataSets.DAKKO_PAYSLIP_DS:
      return { CompanyCode: organizationNumber };
    case DataLoaderDataSets.PAYROLL_DS:
      return { orgId };
    default:
      break;
  }
};

export const dataLoaderApi = createApi({
  reducerPath: "dataLoaderApi",
  baseQuery: baseQueryCreator(baseQuery),
  tagTypes: ["requestDetails"],
  endpoints: (builder) => ({
    uploadData: builder.mutation<
      UploadFileResponse,
      {
        // inputFile?: File;
        fileName?: string;
        organisationNumber?: string;
        datasetName?: string;
        orgId?: string;
        uploadType?: string;
        documentId?: string;
        entityId?: string;
        orgToken: string;
        disableDeleteStatement?: string
      }
    >({
      query: ({
        // inputFile,
        fileName,
        organisationNumber,
        datasetName = DataLoaderDataSets.DAKKO_PAYSLIP_DS,
        orgId,
        uploadType = "RDB",
        documentId,
        entityId,
        orgToken,
        disableDeleteStatement
      }) => {
        const formData = new FormData();
        if (fileName) formData.append("fileName", fileName);
        if (uploadType) formData.append("uploadType", uploadType);
        if (documentId) formData.append("documentId", documentId);
        if (entityId) formData.append("entityId", entityId);
        if (disableDeleteStatement) formData.append("disableDeleteStatement", disableDeleteStatement);

        formData.append("datasetName", datasetName);
        if(organisationNumber && orgId) {
          formData.append(
          "metaData",
          JSON.stringify(getMetaData(datasetName, organisationNumber, orgId))
        );
        }
        formData.append("validateDataFileOnly", "false");
        return {
          url: "/files",
          method: "POST",
          headers: {
            'org-Token': orgToken
          },
          body: formData,
        };
      },
    }),
    getBulkPaymentData: builder.query<
      BulkPaymentItemListResponse,
      BulkPaymentItemRequest
    >({
      query: (params) => ({
        url: `datasets/upload/${DataLoaderDataSets.PAYROLL_DS}/history`,
        params: {
          ...params,
        },
      }),
    }),
    downloadFile: builder.mutation<DatasetDownloadResponse, DatasetDownloadRequest>({
      query: ({id, orgToken}) => {
        return {
          url: `datasets/download/${id}/results`,
          method: "GET",
          headers: {
            'org-Token': orgToken
          },
        };
      },
    }),
    getRequestDetails: builder.query<FileDetailsReponse, FileDetailsRequest>({
      query: ({requestId, orgToken}) => {
        return {
          url: `/files/${requestId}`,
          method: "GET",
          headers: {
            'org-Token': orgToken
          },
        };
      },
      providesTags: ["requestDetails"],
      keepUnusedDataFor: 2,
    }),
    getUploadMerchantHistories:  builder.query<HistoryFileUploadedResponse, HistoryFileUploadedRequest>({
      query: ({ orgToken ,...params}) => {
        return {
          url: `/datasets/upload/${DataLoaderDataSets.TPPP_MERCHANT}/history`,
          method: "GET",
          headers: {
            'org-Token': orgToken
          },
          params
        };
      }
    }),
     getUploadTransactionFiles:  builder.query<HistoryFileUploadedResponse, HistoryFileUploadedRequest>({
      query: ({ orgToken ,...params}) => {
        return {
          url: `/datasets/upload/${DataLoaderDataSets.TPPP_TRANSACTION_RECON_DATA}/history`,
          method: "GET",
          headers: {
            'org-Token': orgToken
          },
          params
        };
      }
    })
  }),
});

export const {
  useUploadDataMutation,
  useGetBulkPaymentDataQuery,
  useDownloadFileMutation,
  useGetRequestDetailsQuery,
  useGetUploadMerchantHistoriesQuery,
  useGetUploadTransactionFilesQuery
} = dataLoaderApi;